import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useOutletContext, Link } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { MessageCircle, ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";

const LS_KEY = "mmm_chat";
const GUEST_KEY = "mmm_guest_token";

export default function ChatContinue() {
  const [params] = useSearchParams();
  const { content, openChat } = useOutletContext();
  const [status, setStatus] = useState("loading"); // loading | ready | invalid
  const [visitorName, setVisitorName] = useState("");

  const session = params.get("session");
  const token = params.get("ct") || params.get("token");
  const guest = params.get("guest");

  useEffect(() => {
    document.title = "Continue your conversation · MovingMyMama";
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { stored = null; }
    const sid = session || stored?.session_id;
    const tok = token || stored?.token;
    if (!sid || !tok) { setStatus("invalid"); return; }
    if (guest) localStorage.setItem(GUEST_KEY, guest);
    publicApi.get(`/public/chat/${sid}`, { params: { token: tok, after: 0 } })
      .then(({ data }) => {
        localStorage.setItem(LS_KEY, JSON.stringify({ session_id: sid, token: tok }));
        const firstVisitor = (data.messages || []).find((m) => m.role === "visitor");
        setVisitorName(data.service_lane_label || "");
        setStatus("ready");
      })
      .catch(() => setStatus("invalid"));
  }, [session, token, guest]);

  const canDo = useMemo(() => ([
    { ok: true, text: "Continue this conversation with your guide" },
    { ok: true, text: "Join an assigned video / meeting room later" },
    { ok: true, text: "View and upload documents assigned to you" },
    { ok: true, text: "Create a free member account anytime to save everything" },
  ]), []);
  const cannotDo = [
    "See other families or their information",
    "Access staff or admin screens",
    "Open documents that aren't shared with you",
  ];

  return (
    <div data-testid="chat-continue-page" className="py-16 sm:py-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {status === "loading" && (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>
        )}
        {status === "invalid" && (
          <div className="text-center" data-testid="chat-continue-invalid">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#9C1E22]/10 text-[#9C1E22]"><Lock size={30} /></span>
            <h1 className="mt-6 text-3xl font-display font-bold text-[#1C4358]">This link has expired</h1>
            <p className="mt-3 text-lg text-[#4B5563]">For your privacy, continuation links don't last forever. You can start a fresh conversation anytime.</p>
            <button onClick={() => openChat && openChat({ cta_source: "Continue Expired" })} data-testid="chat-continue-restart"
              className="mmm-tap mt-6 inline-flex items-center gap-2 px-7 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors">
              <MessageCircle size={19} /> Start a new chat
            </button>
            <div className="mt-4"><Link to="/" className="text-[#1C4358] hover:underline text-[15px]">Back to home</Link></div>
          </div>
        )}
        {status === "ready" && (
          <div className="text-center mmm-fade-up" data-testid="chat-continue-ready">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#2F7D5A]/12 text-[#2F7D5A]"><CheckCircle2 size={30} /></span>
            <h1 className="mt-6 text-3xl sm:text-4xl font-display font-bold text-[#1C4358]">Welcome back — let's keep going.</h1>
            <p className="mt-3 text-lg text-[#252221] leading-relaxed">
              Your conversation is right where you left it{visitorName ? ` (${visitorName})` : ""}. A caring MovingMyMama guide is here to help your family with the next right step.
            </p>
            <button onClick={() => openChat && openChat({ cta_source: "Continue Chat" })} data-testid="chat-continue-open"
              className="mmm-tap mt-6 inline-flex items-center gap-2 px-8 h-14 rounded-full text-[17px] font-semibold text-white bg-[#9C1E22] hover:bg-[#861A1D] transition-colors">
              <MessageCircle size={19} /> Continue my conversation
            </button>

            <div className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
              <div className="rounded-[0.9rem] bg-[#FBF6EC] border border-[#1C4358]/10 p-5">
                <div className="flex items-center gap-2 font-display font-semibold text-[#2F7D5A]"><ShieldCheck size={17} /> You can</div>
                <ul className="mt-2 space-y-1.5">
                  {canDo.map((c, i) => <li key={i} className="flex items-start gap-2 text-[14px] text-[#252221]"><CheckCircle2 size={15} className="text-[#2F7D5A] mt-0.5 shrink-0" /> {c.text}</li>)}
                </ul>
              </div>
              <div className="rounded-[0.9rem] bg-white border border-[#1C4358]/10 p-5">
                <div className="flex items-center gap-2 font-display font-semibold text-[#1C4358]"><Lock size={16} /> For everyone's privacy</div>
                <ul className="mt-2 space-y-1.5">
                  {cannotDo.map((c, i) => <li key={i} className="flex items-start gap-2 text-[14px] text-[#4B5563]"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#9C1E22] shrink-0" /> {c}</li>)}
                </ul>
              </div>
            </div>
            {content?.phone && (
              <p className="mt-6 text-[15px] text-[#4B5563]">Prefer to talk? Call <a href={`tel:${content.phone.replace(/[^0-9+]/g, "")}`} className="font-semibold text-[#9C1E22]">{content.phone}</a>.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
